import React, { useMemo, useRef } from 'react';
import { getContentWithoutBreakLineStart, getDetailLetter } from '../../../../utils/utils';
import { ExamPartQuestion } from '../../../../model/response';
import { EditOutlined } from '@ant-design/icons';
import '../../../../css/component/questionDetail.css';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'redux/store/store';
import { showEditQuestionModal } from '../../../../redux/slice/editQuestionModalSlice';
import { findQuestionImportRequest } from 'service/questionService';
import processError from 'utils/processError';

interface QuestionDetailViewProps {
  examPartQuestion: ExamPartQuestion;
  children?: ExamPartQuestion[];
  scrollToQuestion?: (questionElement: HTMLDivElement) => void;
  groupIndex?: number;
  examPartQuestionIndexMap: Map<string, number>;
}

function QuestionDetailView({
  examPartQuestion,
  scrollToQuestion,
  children,
  groupIndex,
  examPartQuestionIndexMap,
}: QuestionDetailViewProps) {
  const questionRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const setEditQuestionRedux = async () => {
    try {
      const question = await findQuestionImportRequest(examPartQuestion.question.id);
      if (question?.questionDetails.length > 1) {
        question.questionDetails = question.questionDetails.sort(
          (d1, d2) => d1.questionDetailIndex - d2.questionDetailIndex,
        );
      }
      dispatch(showEditQuestionModal(question));
    } catch (err) {
      processError(err);
    }
  };

  const prefix = useMemo(() => {
    if (groupIndex !== undefined && examPartQuestion.question.type === 'YES_NO_QUESTION') {
      return `<span class='font-medium'>${getDetailLetter(groupIndex, 'YES_NO_QUESTION')} </span>`;
    }
    const questionIndex = examPartQuestionIndexMap.get(examPartQuestion.id);
    return questionIndex === undefined ? '' : `<span class='font-bold'>Câu ${questionIndex + 1}. </span>`;
  }, [examPartQuestion.id, examPartQuestion.question.type, examPartQuestionIndexMap, groupIndex]);

  const getSortQuestionDetails = useMemo(() => {
    if (examPartQuestion.question.questionDetails.length >= 2) {
      return examPartQuestion.question.questionDetails.sort(
        (d1, d2) => d1.questionDetailIndex - d2.questionDetailIndex,
      );
    } else {
      return examPartQuestion.question.questionDetails;
    }
  }, [examPartQuestion.question.questionDetails]);

  return (
    <div
      ref={questionRef}
      className={'p-1 rounded-lg focus:border-1 focus:border-medium-grey relative bound-question-detail'}
      tabIndex={0}
    >
      <button
        className="rounded cursor-pointer absolute hidden button-edit right-0 top-0 bg-white"
        onClick={() => setEditQuestionRedux()}
      >
        <EditOutlined />
      </button>
      <div
        dangerouslySetInnerHTML={{
          __html: prefix + getContentWithoutBreakLineStart(examPartQuestion.question.topic),
        }}
      />
      <div className={'ml-4 mt-1 flex flex-col gap-y-1'}>
        {getSortQuestionDetails.map((detail, index) => (
          <div key={detail.id}>
            <div
              dangerouslySetInnerHTML={{
                __html:
                  `<span class='font-medium'>${getDetailLetter(index, examPartQuestion.question.type)} </span>` +
                  getContentWithoutBreakLineStart(detail.description),
              }}
            />
          </div>
        ))}
      </div>
      {children ? (
        <div className={'ml-4 mt-2 flex flex-col gap-y-1'}>
          {children.map((child, index) => (
            <QuestionDetailView
              examPartQuestion={child}
              key={child.id}
              groupIndex={index}
              scrollToQuestion={scrollToQuestion}
              examPartQuestionIndexMap={examPartQuestionIndexMap}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default QuestionDetailView;
